// src/lib/guided/order.ts
//
// Phase 2.3 of docs/guided-shopping_plan.md: at each point in the path,
// pick the criterion whose answers best split the offers still possible,
// weighted by an answerability prior (research §3.4, plan §3a.6).
//
// Scope, precisely: this module answers "which criterion, if any, helps
// choose between the offers still on the table" -- nothing else. It
// deliberately returns nothing for a criterion whose buckets never change
// which offers survive (2.2's WEB_AGENCY_CONFIG fixture is a real example:
// `pages` and `budget` are each relevant to only one offer and every one of
// their buckets is accepted, so neither discriminates -- `buildQuestionOrder`
// on that config returns null). That is correct for this task's acceptance
// ("a criterion that does not separate offers is never asked"), not a bug:
// grading (Phase 3) reads `criterion.type` generically and does not go
// through this module, and 2.4 ("Express the tree as hidden steps") owns
// deciding how a criterion that is relevant to exactly one already-chosen
// offer still gets asked once the offer picker has narrowed to it.
import type {
  CriterionType,
  FunnelConfig,
  FunnelCriterion,
  FunnelOfferFit,
} from "./config";
import { URGENCY_FAMILY_TYPES } from "./compile";

export interface OrderedQuestion {
  criterionId: string;
  /** Keyed by the chosen bucket's id; `null` means the path stops here. */
  branches: Record<string, OrderedQuestion | null>;
}

/**
 * Plan 2.3's answerability prior: `gate` and the urgency family are as
 * answerable as a question gets (1.0), `fit` is close behind (0.9), `money`
 * is the most likely to stall a visitor (0.6). The plan does not weight the
 * remaining types (`authorityGate`, `severity`, `priorAttempts`, `priority`,
 * `hesitation`, `outcomePriority`); 0.8 sits between fit and money rather
 * than inventing a tier the plan never specified.
 */
export function answerabilityWeight(type: CriterionType): number {
  if (type === "gate") return 1.0;
  if ((URGENCY_FAMILY_TYPES as readonly CriterionType[]).includes(type)) {
    return 1.0;
  }
  if (type === "fit") return 0.9;
  if (type === "money") return 0.6;
  return 0.8;
}

/** Shannon entropy of a uniform distribution over `offerIds`; 0 offers is as resolved as 1. */
function entropy(offerIds: readonly string[]): number {
  return offerIds.length <= 1 ? 0 : Math.log2(offerIds.length);
}

/**
 * Whether `offerId` is still possible after answering `bucketId` on
 * `criterionId`, per `offerFit`. Absent from `offerFit` entirely, or the
 * criterion absent from the offer's entry, both mean "not affecting this
 * offer's fit" (config.ts's own doc comment on `OfferFitSchema`) -- the
 * offer survives every bucket of a criterion it does not mention.
 */
export function offerSurvivesBucket(
  offerId: string,
  criterionId: string,
  bucketId: string,
  offerFit: FunnelOfferFit,
): boolean {
  const acceptedBucketIds = offerFit[offerId]?.[criterionId];
  if (!acceptedBucketIds) return true;
  return acceptedBucketIds.includes(bucketId);
}

/**
 * Exported for 2.4 (`compile.ts`): the compiler re-applies this same
 * per-bucket survivor rule both to continue narrowing after a criterion this
 * module chose, and to build a step's FieldLogic conditions, so it needs the
 * identical rule this module's own recursion uses rather than a second copy.
 */
export function survivorsForBucket(
  criterionId: string,
  bucketId: string,
  remainingOfferIds: readonly string[],
  offerFit: FunnelOfferFit,
): string[] {
  return remainingOfferIds.filter((offerId) =>
    offerSurvivesBucket(offerId, criterionId, bucketId, offerFit),
  );
}

/**
 * Expected reduction in offer-identity entropy from asking `criterion`,
 * assuming each of its buckets is equally likely to be the answer (there is
 * no visitor data to weight them by; §3.4's own cited result, Rashid et al.
 * 2002, needed real usage logs we don't have here). Zero exactly when every
 * bucket leaves `remainingOfferIds` unchanged -- the criterion does not
 * bear on which of these offers fits, whether because `offerFit` never
 * mentions it or because it mentions it without excluding anyone.
 */
function informationGain(
  criterion: FunnelCriterion,
  remainingOfferIds: readonly string[],
  offerFit: FunnelOfferFit,
): number {
  const before = entropy(remainingOfferIds);
  const bucketProbability = 1 / criterion.buckets.length;
  const expectedAfter = criterion.buckets.reduce((sum, bucket) => {
    const survivors = survivorsForBucket(
      criterion.id,
      bucket.id,
      remainingOfferIds,
      offerFit,
    );
    return sum + bucketProbability * entropy(survivors);
  }, 0);
  return before - expectedAfter;
}

/**
 * The single best next criterion among `candidates`, or `null` when none of
 * them separate `remainingOfferIds` at all (plan 2.3: "drop a criterion on
 * any branch where all remaining offers agree on it"). Ties in raw
 * information gain are broken by `answerabilityWeight` alone, so a `fit`
 * criterion always outranks an equally informative `money` one; a tie in
 * the final weighted score keeps the first candidate in `candidates` order.
 */
export function chooseNextCriterion(
  candidates: readonly FunnelCriterion[],
  remainingOfferIds: readonly string[],
  offerFit: FunnelOfferFit,
): FunnelCriterion | null {
  let best: FunnelCriterion | null = null;
  let bestScore = 0;
  for (const criterion of candidates) {
    const gain = informationGain(criterion, remainingOfferIds, offerFit);
    if (gain <= 0) continue;
    const score = gain * answerabilityWeight(criterion.type);
    if (score > bestScore) {
      bestScore = score;
      best = criterion;
    }
  }
  return best;
}

function assertUniqueCriterionIds(criteria: readonly FunnelCriterion[]): void {
  const seen = new Set<string>();
  for (const criterion of criteria) {
    if (seen.has(criterion.id)) {
      throw new Error(`Duplicate criterion id: "${criterion.id}"`);
    }
    seen.add(criterion.id);
  }
}

function chooseOrderNode(
  config: FunnelConfig,
  remainingOfferIds: readonly string[],
  askedCriterionIds: ReadonlySet<string>,
  depth: number,
): OrderedQuestion | null {
  // "Stop when one offer (or the polite no) remains, or at maxQuestions."
  if (remainingOfferIds.length <= 1) return null;
  if (depth >= config.maxQuestions) return null;

  const candidates = config.criteria.filter(
    (criterion) => !askedCriterionIds.has(criterion.id),
  );
  const next = chooseNextCriterion(
    candidates,
    remainingOfferIds,
    config.offerFit,
  );
  if (!next) return null;

  const nextAsked = new Set(askedCriterionIds);
  nextAsked.add(next.id);

  const branches: Record<string, OrderedQuestion | null> = {};
  for (const bucket of next.buckets) {
    const survivors = survivorsForBucket(
      next.id,
      bucket.id,
      remainingOfferIds,
      config.offerFit,
    );
    branches[bucket.id] = chooseOrderNode(
      config,
      survivors,
      nextAsked,
      depth + 1,
    );
  }

  return { criterionId: next.id, branches };
}

/**
 * The full decision tree of which criterion to ask next, branching per
 * bucket, starting from every offer being possible. `null` means no
 * criterion in `config.criteria` ever separates the offer set -- either
 * there is nothing to ask (one offer, or none), or none of the criteria
 * bear on offer choice at all (see this file's header comment).
 */
export function buildQuestionOrder(
  config: FunnelConfig,
): OrderedQuestion | null {
  assertUniqueCriterionIds(config.criteria);
  const allOfferIds = config.offers.map((offer) => offer.id);
  return chooseOrderNode(config, allOfferIds, new Set(), 0);
}
